import createAsyncGetters, {
  type AsyncGetterAPI,
} from '../config/asyncGetters/createAsyncGetters';
import type { State } from '../State';
import type { AvatarFrameId } from '../profile/frames';
import {
  getPlayerProfile,
  getPlayerProfileAvatarFrame,
} from '../profile/profile.getters';
import {
  getBuruFriends,
  getUnfriendedFriends,
  getUsersWithPendingRequest,
} from '../friends/friends.getters';
import {
  cardsGetMissingCardIds,
  cardsGetOwnedCards,
  cardsGetOwnedCardCount,
  cardsGetTradeableCards,
  cardsIsCardValid,
  cardsIsEnabled,
} from './cards.getters';
import {
  cardsRequestCandidateLimit,
  cardsTradeCandidateLimit,
  type CardId,
  type CardsInventory,
} from './cards.ruleset';
import { createReportableError } from '../utils/errors';

export type CardsFriend = {
  id: string;
  name: string;
  photo: string;
  frame: AvatarFrameId;
  cardsOwned: CardsInventory;
};

export type CardsFriendMap = Record<string, CardsFriend>;

export type CardsCandidate = {
  id: string;
  name: string;
  photo: string;
};

export const cardsAsyncGetters = createAsyncGetters({
  /**
   * Fetch player data for a list of player IDs.
   */
  cardsGetFriends: async (args, api) => {
    const state = await api.getOwnState();
    const friendsIds = getBuruFriends(state);
    const entries = await api.fetchStates(friendsIds);
    const friendsMap: CardsFriendMap = {};

    for (const playerId in entries) {
      if (!entries[playerId]) continue;

      const playerState = entries[playerId].state;

      // Do not include friends that have the cards collection feature disabled.
      if (!cardsIsEnabled(playerState)) {
        continue;
      }

      const profile = getPlayerProfile(
        playerState,
        playerState.publicProfile?.name,
        playerState.publicProfile?.photo,
      );

      friendsMap[playerId] = {
        id: playerId,
        name: profile.name,
        photo: profile.photo,
        frame: getPlayerProfileAvatarFrame(playerState),
        cardsOwned: cardsGetOwnedCards(playerState),
      };
    }

    return friendsMap;
  },

  /**
   * Search for players that can be shown in the card trade picker.
   *
   * Direct friends and friends-of-friends with public profiles are ranked by
   * recent activity and must have an extra copy of a card the player is missing.
   * Direct friends are returned first, followed by FOFs, with the combined
   * result capped at five players.
   */
  cardsGetTradeCandidates: async (
    args: { cardId: CardId },
    api,
  ): Promise<CardsCandidate[]> => {
    if (!cardsIsCardValid(args.cardId)) {
      throw new Error(`Unknown card ${args.cardId}.`);
    }

    const state = await api.getOwnState();
    const now = api.date.now();

    // No card-trade candidates are available while the collection feature is disabled.
    if (!cardsIsEnabled(state)) return [];

    // The player can only start a trade with a card they have extra copies of.
    const tradeableCards = cardsGetTradeableCards(state, now);
    if (!tradeableCards[args.cardId]) return [];

    // Only players who can offer a missing card are useful trade candidates.
    const wantedCardIds = cardsGetMissingCardIds(state, now);
    if (wantedCardIds.length === 0) return [];

    // Search the social cluster for players who can offer a missing card.
    return searchCardCandidates({
      state,
      api,
      cardIds: wantedCardIds,
      limit: cardsTradeCandidateLimit,
    });
  },

  /**
   * Search for players that can be shown in the card request picker.
   *
   * Direct friends and friends-of-friends with public profiles are ranked by
   * recent activity and must have an extra copy of the requested card. Direct
   * friends are returned first, followed by FOFs, with the combined result
   * capped at 20 players.
   */
  cardsGetRequestCandidates: async (
    args: { cardId: CardId },
    api,
  ): Promise<CardsCandidate[]> => {
    if (!cardsIsCardValid(args.cardId)) {
      throw new Error(`Unknown card ${args.cardId}.`);
    }

    const state = await api.getOwnState();
    const now = api.date.now();

    // No card-request candidates are available while the collection feature is disabled.
    if (!cardsIsEnabled(state)) return [];

    // The player should only request cards they do not already own.
    if (cardsGetOwnedCardCount(state, args.cardId, now) > 0) return [];

    // Search the social cluster for players who can offer the requested card.
    return searchCardCandidates({
      state,
      api,
      cardIds: [args.cardId],
      limit: cardsRequestCandidateLimit,
    });
  },
});

type CardsSearchPlayer = {
  id: string;
  publicProfileName?: string;
  publicProfilePhoto?: string;
};

/** Inputs used to search direct friends and friends-of-friends for card candidates. */
type SearchCardCandidatesArgs = {
  /** Current player state used to resolve friends, exclusions, and profile defaults. */
  state: State;
  /** Async-getter API used to search players and report partial failures. */
  api: AsyncGetterAPI;
  /** Card IDs candidates must have an extra copy of. */
  cardIds: CardId[];
  /** Maximum number of merged candidates returned to the caller. */
  limit: number;
};

const cardsCandidateFields = [
  'publicProfileName',
  'publicProfilePhoto',
] as const;

/** Maximum number of friend ids used to seed a card-candidate FOF search. */
const cardsCandidateClusterFriendCap = 100;

/** Number of rows fetched per card-candidate search before merging. */
const cardsCandidateSearchLimit = 20;

/**
 * Convert search result rows into the compact public profile data expected by
 * the card candidate pickers.
 *
 * @param state Current player state used for locale-aware profile defaults.
 * @param players Player-search rows to convert.
 * @returns Compact card candidate profiles.
 */
function toCardCandidates(
  state: State,
  players: CardsSearchPlayer[],
): CardsCandidate[] {
  return players.map((player) => {
    const profile = getPlayerProfile(
      state,
      player.publicProfileName,
      player.publicProfilePhoto,
    );
    return {
      id: player.id,
      name: profile.name,
      photo: profile.photo,
    };
  });
}

/**
 * Search direct friends and friends-of-friends who can offer one of the given
 * cards, prioritizing friends and removing duplicate players.
 *
 * @param args Search state, API, eligible card IDs, and result limit.
 * @returns Public-profile card candidates ordered by social priority and activity.
 */
async function searchCardCandidates({
  state,
  api,
  cardIds,
  limit,
}: SearchCardCandidatesArgs): Promise<CardsCandidate[]> {
  // Candidate discovery requires friends to seed both social searches.
  const friendIds = getBuruFriends(state);
  if (friendIds.length === 0) return [];

  // FOF searches exclude the current player and existing social exclusions.
  const excludedFriendOfFriendIds = [
    state.id,
    ...friendIds,
    ...getUsersWithPendingRequest(state),
    ...getUnfriendedFriends(state),
    ...state.friendsBlacklist,
  ];

  // Start the direct-friend search while the cluster search runs.
  const friendSearchPromise = api
    .searchPlayers({
      where: {
        id: { isOneOf: friendIds },
        hasPublicProfile: { is: true },
        cardsCollectionEnabled: { is: true },
        cardsTradeableCardIds: { containsAnyOf: cardIds },
      },
      sort: [{ field: 'updatedAt', order: 'desc' }],
      fields: [...cardsCandidateFields],
      limit: Math.min(friendIds.length, cardsCandidateSearchLimit),
    })
    .catch((error) => {
      api.reportError(createReportableError('error', error as Error));
      return { results: [] };
    });

  // Search the social cluster and degrade to direct friends if it fails.
  const friendOfFriendSearchPromise = api
    .searchPlayers({
      where: {
        friendIds: {
          containsAnyOf: friendIds.slice(0, cardsCandidateClusterFriendCap),
        },
        id: { isNotOneOf: excludedFriendOfFriendIds },
        hasPublicProfile: { is: true },
        cardsCollectionEnabled: { is: true },
        cardsTradeableCardIds: { containsAnyOf: cardIds },
      },
      sort: [{ field: 'updatedAt', order: 'desc' }],
      fields: [...cardsCandidateFields],
      limit: cardsCandidateSearchLimit,
    })
    .catch((error) => {
      api.reportError(createReportableError('error', error as Error));
      return { results: [] };
    });

  // Wait for both sources before merging friends first.
  const [friendSearch, friendOfFriendSearch] = await Promise.all([
    friendSearchPromise,
    friendOfFriendSearchPromise,
  ]);
  const friendCandidates = toCardCandidates(state, friendSearch.results);
  const friendOfFriendCandidates = toCardCandidates(
    state,
    friendOfFriendSearch.results,
  );

  // Merge friends first, dropping duplicate search rows before applying the cap.
  const candidates: CardsCandidate[] = [];
  const seenCandidateIds = new Set<string>();
  for (const candidate of [...friendCandidates, ...friendOfFriendCandidates]) {
    if (seenCandidateIds.has(candidate.id)) continue;

    seenCandidateIds.add(candidate.id);
    candidates.push(candidate);
    if (candidates.length >= limit) break;
  }

  // Return the prioritized candidates from both social sources.
  return candidates;
}
